import React from 'react';
import companyLogo from '../../assets/parakshtech_logo.jpg';
import signatureImg from '../../assets/signature.png';
import stampImg from '../../assets/stamp.png';

const formatDisplayDate = (dStr) => {
  if (!dStr) return '01 February 2026';
  try {
    const d = new Date(dStr);
    if (isNaN(d.getTime())) return dStr;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return dStr;
  }
};

export default function ExperienceCertificate({ data = {} }) {
  const candidateName = data.candidate_name || 'Mr. Alok Kumar';
  const position = data.position || 'UI Designer';
  const joiningDateFormatted = formatDisplayDate(data.joining_date || '2026-02-01');
  const endDateFormatted = formatDisplayDate(data.end_date || new Date().toISOString().slice(0, 10));
  const referenceNo = data.reference_no || 'PT/EXP/2026/001';
  const signatoryTitle = data.signatory_title || 'Parakshtech LLP';
  const signatoryName = data.signatory_name || 'Authorized Signatory';
  const customTerms = data.custom_terms;
  const hasSignature = Boolean(data.include_signature);
  const hasStamp = Boolean(data.include_stamp);

  return (
    <div className="printable-letter-container" style={styles.outerWrapper}>
      <div style={styles.page}>
        <div style={styles.certificate}>

          {/* DECORATIVE CORNERS */}
          <div style={{ ...styles.corner, ...styles.topLeft }} />
          <div style={{ ...styles.corner, ...styles.topRight }} />
          <div style={{ ...styles.corner, ...styles.bottomLeft }} />
          <div style={{ ...styles.corner, ...styles.bottomRight }} />

          {/* COMPANY HEADER */}
          <div style={styles.header}>
            <div style={styles.brandRow}>
              {/* COMPANY LOGO */}
              <img
                src={companyLogo}
                alt="Parakshtech LLP Logo"
                style={styles.logo}
                onError={(e) => { e.target.src = '/logo.jpeg'; }}
              />

              {/* COMPANY NAME */}
              <div style={styles.brandText}>
                <h1 style={styles.companyName}>
                  PARAKSHTECH LLP
                </h1>
                <div style={styles.companySubtitle}>
                  CRAFTING POWERFUL WEB &amp; CLOUD SOLUTIONS
                </div>
              </div>
            </div>
            <div style={styles.headerLine} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#555', marginTop: '6px' }}>
            <span><strong>Ref:</strong> {referenceNo}</span>
            <span><strong>Date:</strong> {formatDisplayDate(data.issue_date || new Date().toISOString().slice(0, 10))}</span>
          </div>

          {/* TITLE */}
          <div style={styles.titleSection}>
            <h2 style={styles.title}>
              Experience Certificate
            </h2>
          </div>

          {/* MAIN CONTENT */}
          <div style={styles.content}>
            <p style={styles.paragraph}>
              This is to certify that{' '}
              <span style={styles.employeeName}>
                {candidateName}
              </span>{' '}
              worked with{' '}
              <strong>
                Parakshtech LLP
              </strong>{' '}
              as a{' '}
              <span style={styles.designation}>
                {position}
              </span>.
            </p>

            {data.body_content && data.body_content.trim() ? (
              data.body_content.split(/\n\s*\n/).filter(p => p.trim()).map((para, idx) => (
                <p key={idx} style={styles.paragraph}>
                  {para.trim()}
                </p>
              ))
            ) : (
              <>
                <p style={styles.paragraph}>
                  During the tenure with the organization, they were
                  responsible for designing, developing, and executing
                  key software components, maintaining high technical standards,
                  and collaborating proactively with cross-functional teams to
                  deliver superior digital solutions.
                </p>

                <p style={styles.paragraph}>
                  They demonstrated exceptional professionalism, dedication,
                  technical prowess, and a collaborative team attitude throughout
                  their employment with the organization.
                </p>
              </>
            )}
          </div>

          {/* EMPLOYMENT DETAILS */}
          <table style={styles.detailsTable}>
            <tbody>
              <tr>
                <td style={styles.detailsTd}>Employee Name</td>
                <td style={styles.detailsTd}><strong>{candidateName}</strong></td>
              </tr>
              <tr>
                <td style={styles.detailsTd}>Designation</td>
                <td style={styles.detailsTd}>{position}</td>
              </tr>
              <tr>
                <td style={styles.detailsTd}>Joining Date</td>
                <td style={styles.detailsTd}>{joiningDateFormatted}</td>
              </tr>
              <tr>
                <td style={styles.detailsTd}>Relieving Date</td>
                <td style={styles.detailsTd}>{endDateFormatted}</td>
              </tr>
              <tr>
                <td style={styles.detailsTd}>Organization</td>
                <td style={styles.detailsTd}>Parakshtech LLP</td>
              </tr>
            </tbody>
          </table>

          {/* CLOSING PARAGRAPH */}
          <div style={styles.closing}>
            {customTerms ? (
              <p style={styles.closingParagraph}>{customTerms}</p>
            ) : (
              <p style={styles.closingParagraph}>
                We appreciate their contributions to the organization
                and wish them all the very best for their future career
                and professional endeavors.
              </p>
            )}
            <p style={styles.closingParagraph}>
              This certificate is issued upon request for record and verification purposes.
            </p>
          </div>

          {/* AUTHORIZED SIGNATORY & OFFICIAL STAMP */}
          <div style={styles.signatureArea}>
            <div style={styles.stampBlock}>
              {hasStamp && (
                <img
                  src={stampImg}
                  alt="ParakshTech LLP Stamp"
                  style={styles.stampImg}
                  onError={(e) => { e.target.src = '/stamp.png'; }}
                />
              )}
            </div>

            <div style={styles.signature}>
              <div style={styles.signatureImgWrapper}>
                {hasSignature ? (
                  <img
                    src={signatureImg}
                    alt="Authorized Signatory"
                    style={styles.signatureImg}
                    onError={(e) => { e.target.src = '/signature.png'; }}
                  />
                ) : (
                  <div style={{ height: '44px' }} />
                )}
              </div>
              <div style={styles.signatureLine} />
              <div style={styles.authorized}>
                {signatoryName}
              </div>
              <div style={styles.companySmall}>
                {signatoryTitle}
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div style={styles.footer}>
            <strong>
              PARAKSHTECH LLP
            </strong>
            {' '}&nbsp;|&nbsp;{' '}
            Pushtakalya Road, Buxar, Bihar - 802101
            <br />
            Email:{' '}
            <strong>
              hr@parakshtech.com
            </strong>
            {' '}&nbsp;|&nbsp;{' '}
            Contact:{' '}
            <strong>
              9296454675
            </strong>
            {' '}&nbsp;|&nbsp;{' '}
            LLPIN:{' '}
            <strong>
              ACV5220
            </strong>
          </div>

        </div>
      </div>
    </div>
  );
}

// Inline Styles Object
const styles = {
  outerWrapper: {
    display: 'flex',
    justifyContent: 'center',
    background: '#f1f5f9',
    padding: '20px 0',
  },
  page: {
    width: '210mm',
    minHeight: '297mm',
    height: '297mm',
    background: '#ffffff',
    padding: '12mm',
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'border-box',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    WebkitPrintColorAdjust: 'exact',
  },
  certificate: {
    border: '4px double #1f3a5f',
    height: '100%',
    padding: '28px 36px',
    position: 'relative',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  corner: {
    position: 'absolute',
    width: '28px',
    height: '28px',
    borderColor: '#c69214',
    borderStyle: 'solid',
  },
  topLeft: {
    top: '8px',
    left: '8px',
    borderWidth: '3px 0 0 3px',
  },
  topRight: {
    top: '8px',
    right: '8px',
    borderWidth: '3px 3px 0 0',
  },
  bottomLeft: {
    bottom: '8px',
    left: '8px',
    borderWidth: '0 0 3px 3px',
  },
  bottomRight: {
    bottom: '8px',
    right: '8px',
    borderWidth: '0 3px 3px 0',
  },
  header: {
    textAlign: 'center',
    marginBottom: '8px',
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '14px',
  },
  logo: {
    width: '56px',
    height: '56px',
    objectFit: 'contain',
  },
  brandText: {
    textAlign: 'left',
  },
  companyName: {
    margin: 0,
    fontSize: '24px',
    letterSpacing: '2px',
    color: '#1f3a5f',
    fontFamily: 'Georgia, serif',
    fontWeight: 'bold',
  },
  companySubtitle: {
    fontSize: '9.5px',
    letterSpacing: '1.5px',
    color: '#666666',
    marginTop: '2px',
    fontWeight: 'bold',
  },
  headerLine: {
    height: '2px',
    background: 'linear-gradient(to right, transparent, #c69214, transparent)',
    marginTop: '10px',
  },
  titleSection: {
    textAlign: 'center',
    margin: '10px 0 14px 0',
  },
  title: {
    margin: 0,
    fontSize: '22px',
    letterSpacing: '3px',
    textTransform: 'uppercase',
    color: '#1f3a5f',
    fontFamily: 'Georgia, serif',
    display: 'inline-block',
    borderBottom: '2px solid #c69214',
    paddingBottom: '4px',
    fontWeight: 'bold',
  },
  content: {
    fontSize: '13px',
    lineHeight: '1.65',
    textAlign: 'justify',
  },
  paragraph: {
    margin: '0 0 8px 0',
  },
  employeeName: {
    fontWeight: 'bold',
    color: '#1f3a5f',
    fontSize: '14px',
  },
  designation: {
    fontWeight: 'bold',
    color: '#111827',
  },
  detailsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    margin: '10px 0',
    fontSize: '12.5px',
  },
  detailsTd: {
    border: '1px solid #d1d5db',
    padding: '7px 12px',
  },
  closing: {
    fontSize: '12.5px',
    lineHeight: '1.55',
  },
  closingParagraph: {
    margin: '0 0 6px 0',
  },
  signatureArea: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: '8px',
    padding: '0 20px',
  },
  stampBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '120px',
    height: '62px',
  },
  stampImg: {
    maxHeight: '62px',
    maxWidth: '115px',
    objectFit: 'contain',
    filter: 'contrast(1.05)',
  },
  stampText: {
    fontSize: '9px',
    fontWeight: 'bold',
    color: '#6b7280',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    marginTop: '3px',
  },
  signature: {
    textAlign: 'center',
    width: '190px',
  },
  signatureImgWrapper: {
    height: '46px',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    marginBottom: '2px',
  },
  signatureImg: {
    maxHeight: '44px',
    maxWidth: '180px',
    objectFit: 'contain',
  },
  signatureLine: {
    width: '100%',
    height: '1.5px',
    backgroundColor: '#1f3a5f',
    margin: '3px auto 4px auto',
  },
  authorized: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#1f3a5f',
  },
  companySmall: {
    fontSize: '10.5px',
    color: '#4b5563',
  },
  footer: {
    borderTop: '1px solid #d1d5db',
    paddingTop: '8px',
    textAlign: 'center',
    fontSize: '9.5px',
    color: '#4b5563',
    lineHeight: '1.5',
  },
};